/**
 * Dynamic Segment Results
 * Displays the results of a dynamic segmentation with selectable segments
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users, CheckCircle2 } from 'lucide-react';
import { Segmentation, DynamicSegment } from '../../services/segmentationService';

interface DynamicSegmentResultsProps {
  segmentation: Segmentation;
  onSelectSegment?: (segmentIndex: number, personaIds: number[]) => void;
  selectedIndices?: number[];
  showHeader?: boolean;
  className?: string;
}

const DynamicSegmentResults: React.FC<DynamicSegmentResultsProps> = ({
  segmentation,
  onSelectSegment,
  selectedIndices = [],
  showHeader = true,
  className = '',
}) => {
  const [expandedSegment, setExpandedSegment] = useState<number | null>(null);

  const toggleExpand = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedSegment(expandedSegment === idx ? null : idx);
  };

  const handleSegmentClick = (segment: DynamicSegment, idx: number) => {
    if (onSelectSegment) {
      onSelectSegment(idx, segment.persona_ids);
    }
  };

  const isSelected = (idx: number) => selectedIndices.includes(idx);

  // Calculate total selected personas
  const segments = segmentation.segment_definitions || [];
  const selectedPersonaCount = selectedIndices.reduce((acc, idx) => {
    const segment = segments[idx];
    return acc + (segment?.persona_count || 0);
  }, 0);

  return (
    <div className={`mt-6 border-t border-gray-200 pt-6 ${className}`}>
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-[#1C1C1E]">
              Segments by "{segmentation.segmentation_axis}"
            </h4>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {segmentation.total_personas_analyzed} personas analyzed
            </p>
          </div>
          {selectedIndices.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-[#07E5D1]">
              <CheckCircle2 className="w-4 h-4" />
              <span>
                {selectedIndices.length} selected ({selectedPersonaCount} personas)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Analysis Summary */}
      {segmentation.analysis_summary && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-[#374151]">{segmentation.analysis_summary}</p>
        </div>
      )}

      {/* Segments List */}
      <div className="space-y-3">
        {(segmentation.segment_definitions || []).map((segment, idx) => (
          <div
            key={idx}
            onClick={() => handleSegmentClick(segment, idx)}
            className={`border rounded-lg overflow-hidden cursor-pointer transition-all ${
              isSelected(idx)
                ? 'border-[#07E5D1] bg-[#E8FCFA]'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            {/* Segment Header */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3 flex-1">
                {onSelectSegment && (
                  <input
                    type="checkbox"
                    checked={isSelected(idx)}
                    onChange={() => {}}
                    className="w-4 h-4 accent-[#07E5D1] rounded"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                        isSelected(idx) ? 'bg-[#07E5D1] text-white' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <h5
                      className={`font-medium ${
                        isSelected(idx) ? 'text-[#07E5D1]' : 'text-[#1C1C1E]'
                      }`}
                    >
                      {segment.label}
                    </h5>
                  </div>
                  <p
                    className={`text-sm text-[#6B7280] mt-1 ${
                      expandedSegment === idx ? '' : 'line-clamp-2'
                    }`}
                  >
                    {segment.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 ml-4">
                <div className="flex items-center gap-1.5 text-sm text-[#374151]">
                  <Users className="w-4 h-4 text-[#6B7280]" />
                  <span className="font-medium">{segment.persona_count}</span>
                </div>
                <button
                  onClick={(e) => toggleExpand(idx, e)}
                  className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {expandedSegment === idx ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedSegment === idx && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                {segment.defining_characteristics &&
                  segment.defining_characteristics.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide mb-2">
                        Defining Characteristics
                      </p>
                      <ul className="space-y-1.5">
                        {segment.defining_characteristics.map((char, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-[#374151]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#07E5D1] mt-1.5 flex-shrink-0" />
                            {char}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Persona Count Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1">
                    <span>Segment Size</span>
                    <span>
                      {(
                        (segment.persona_count / segmentation.total_personas_analyzed) *
                        100
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        isSelected(idx) ? 'bg-[#07E5D1]' : 'bg-gray-400'
                      }`}
                      style={{
                        width: `${(segment.persona_count / segmentation.total_personas_analyzed) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Selection Summary */}
      {selectedIndices.length > 0 && onSelectSegment && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#374151]">
              <strong>{selectedIndices.length}</strong> segment
              {selectedIndices.length !== 1 ? 's' : ''} selected
            </span>
            <span className="text-[#07E5D1] font-medium">
              {selectedPersonaCount} total personas
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicSegmentResults;
